import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import { Card, CardContent, Typography, Box } from '@mui/material'

import MaterialPricingDisplay from '@src/components/MaterialPricingDisplay'
import PromotionalMessage from '@src/components/PromotionalMessage'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import HandleMarkup from '@src/components/HandleMarkup'

const useStyles = makeStyles()((theme) => ({
  productCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.spacing(1.25),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[2],
    },
  },
  selectedCard: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light,
  },
  cardContent: {
    padding: theme.spacing(2),
    '&:last-of-type': {
      paddingBottom: theme.spacing(2),
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
  },
  packageSize: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
  },
  listPrice: {
    fontSize: theme.typography.pxToRem(14),
  },
  strikePrice: {
    fontSize: theme.typography.pxToRem(12),
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(2),
  },
  availability: {
    fontSize: theme.typography.pxToRem(16),
  },
}))

interface ProductCardProps {
  material: ValidMaterialPricingDetailFragment
  isSelected: boolean
  onSelect: () => void
}

const ProductCard: React.FC<ProductCardProps> = ({
  material,
  isSelected,
  onSelect,
}) => {
  const { classes } = useStyles()
  const packageSize = material?.packageSize?.trim() || material?.materialNumber
  const availabilities = material?.availabilities
  const hasStock = availabilities && availabilities.length > 0
  const stockInfo = hasStock ? availabilities[0] : null

  return (
    <Card
      className={`${classes.productCard} ${isSelected ? classes.selectedCard : ''}`}
      onClick={onSelect}
    >
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <HandleMarkup className={classes.packageSize} value={packageSize} />
          <div>
            <MaterialPricingDisplay
              material={material}
              priceClass={classes.listPrice}
              listPriceClass={classes.strikePrice}
            />
          </div>
        </div>

        {stockInfo && (
          <Typography component="span" className={classes.availability}>
            {stockInfo.quantity && stockInfo.quantity > 0 ? (
              <FormattedMessage id="IN_STOCK" defaultMessage="In Stock" />
            ) : (
              <FormattedMessage
                id="OUT_OF_STOCK_LABEL"
                defaultMessage="Out of Stock"
              />
            )}{' '}
            From {stockInfo.plantLoc}
          </Typography>
        )}
        {material.promotionalMessage?.messageKey && (
          <Box mt={1}>
            <PromotionalMessage promotional={material.promotionalMessage} />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ProductCard
